/**
 * حسابُ التقويم بتوقيت عمّان — بلا مكتبة (11 أكتوبر 2026).
 *
 * المفتاحُ اليومي `YYYY-MM-DD` يُحسب بـ`Intl.DateTimeFormat` على المنطقة نفسِها التي يحسب بها
 * الخادم (`amman.ts`)، فجلسةُ 23:30 لا تقفز إلى الغد على جهازٍ بتوقيتٍ آخر. والأسبوعُ يبدأ **الأحد**
 * (أسبوعُ العمل الأردني). الدوالُ هنا نقيّةٌ ومختبَرةٌ وحدها (`test/calendar.test.ts`).
 */
export const AMMAN = 'Asia/Amman';

const dayKeyFmt = new Intl.DateTimeFormat('en-CA', { timeZone: AMMAN, year: 'numeric', month: '2-digit', day: '2-digit' });
const hmFmt = new Intl.DateTimeFormat('en-GB', { timeZone: AMMAN, hour: '2-digit', minute: '2-digit', hour12: false });
export const timeFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, hour: '2-digit', minute: '2-digit', hour12: false });
export const dayNameFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, weekday: 'long' });
export const dayShortFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, weekday: 'short' });
export const dayNumFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, day: 'numeric', month: 'short' });
export const longDateFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, weekday: 'long', day: 'numeric', month: 'long', year: 'numeric' });
export const monthTitleFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, month: 'long', year: 'numeric' });
export const rangeFmt = new Intl.DateTimeFormat('ar-u-nu-latn', { timeZone: AMMAN, day: 'numeric', month: 'long', year: 'numeric' });

export const MONTHS_AR = ['يناير', 'فبراير', 'مارس', 'أبريل', 'مايو', 'يونيو', 'يوليو', 'أغسطس', 'سبتمبر', 'أكتوبر', 'نوفمبر', 'ديسمبر'];
/** أسماءُ الأيام من الأحد — كترتيب الأعمدة */
export const WEEKDAYS_AR = ['الأحد', 'الاثنين', 'الثلاثاء', 'الأربعاء', 'الخميس', 'الجمعة', 'السبت'];
export const WEEKDAYS_SHORT_AR = ['أحد', 'اثنين', 'ثلاثاء', 'أربعاء', 'خميس', 'جمعة', 'سبت'];

export const pad2 = (n: number) => String(n).padStart(2, '0');

/** مفتاحُ اليوم المحلي (عمّان) للحظةٍ ما */
export const dayKeyOf = (d: Date) => dayKeyFmt.format(d);
/** «HH:mm» بعمّان */
export const hmOf = (d: Date) => hmFmt.format(d).replace(/^24/, '00');
/** دقائقُ اليوم بعمّان */
export function minutesOf(d: Date): number { const [h, m] = hmOf(d).split(':').map(Number); return h * 60 + m; }

export function parseDayKey(key: string): { y: number; m: number; d: number } {
  const [y, m, d] = key.split('-').map(Number);
  return { y, m, d };
}
/** مفتاحُ اليوم بعد `n` يوماً — حسابٌ تقويميٌّ خالص لا يتأثّر بالتوقيت الصيفي */
export function addDays(key: string, n: number): string {
  const { y, m, d } = parseDayKey(key);
  return new Date(Date.UTC(y, m - 1, d + n)).toISOString().slice(0, 10);
}
/** يومُ الأسبوع لمفتاح يوم (0 = الأحد) */
export function weekdayOf(key: string): number {
  const { y, m, d } = parseDayKey(key);
  return new Date(Date.UTC(y, m - 1, d)).getUTCDay();
}
/** أحدُ الأسبوع الذي فيه هذا اليوم */
export const weekStartOf = (key: string) => addDays(key, -weekdayOf(key));
export const monthKeyOf = (key: string) => key.slice(0, 7);
export const daysInMonth = (y: number, m: number) => new Date(Date.UTC(y, m, 0)).getUTCDate();
export function addMonths(monthKey: string, n: number): string {
  const [y, m] = monthKey.split('-').map(Number);
  const t = new Date(Date.UTC(y, m - 1 + n, 1));
  return `${t.getUTCFullYear()}-${pad2(t.getUTCMonth() + 1)}`;
}
export const todayKey = () => dayKeyOf(new Date());

/**
 * اللحظةُ الحقيقية لدقيقةٍ محلية في يومٍ محلي («2026-10-11» + 09:30 بعمّان) — كـ`zonedTime` في الخادم:
 * تخمينٌ بافتراض UTC ثم تصحيحٌ بالفرق الذي يقوله المنسِّق، مرّتين لحافّة التوقيت الصيفي.
 */
export function zonedTime(dayKey: string, minutes: number): Date {
  const { y, m, d } = parseDayKey(dayKey);
  let at = new Date(Date.UTC(y, m - 1, d, Math.floor(minutes / 60), minutes % 60, 0, 0));
  for (let i = 0; i < 2; i++) {
    const k = dayKeyOf(at);
    const driftDays = Math.round((Date.parse(`${k}T00:00:00Z`) - Date.UTC(y, m - 1, d)) / 86_400_000);
    const drift = driftDays * 1440 + minutesOf(at) - minutes;
    if (drift === 0) break;
    at = new Date(at.getTime() - drift * 60_000);
  }
  return at;
}
/** بدايةُ اليوم المحلي كـISO — للنافذة المرسلة إلى الخادم */
export const startOfDayIso = (dayKey: string) => zonedTime(dayKey, 0).toISOString();

/**
 * شبكةُ الشهر: ستةُ أسابيع دائماً (42 يوماً) من أحد الأسبوع الذي فيه أولُ الشهر —
 * ثابتةُ الارتفاع فلا تقفز الصفحةُ بين شهرٍ من خمسة أسابيع وآخر من ستة.
 */
export function monthGrid(monthKey: string): string[][] {
  const first = weekStartOf(`${monthKey}-01`);
  return Array.from({ length: 6 }, (_, w) => Array.from({ length: 7 }, (_, i) => addDays(first, w * 7 + i)));
}
/** نافذةُ الاستعلام لشبكة الشهر: [أولُ خانة، اليومُ بعد آخرها) */
export function monthGridWindow(monthKey: string): { from: string; to: string; firstDay: string; lastDay: string } {
  const grid = monthGrid(monthKey);
  const firstDay = grid[0][0]; const lastDay = grid[5][6];
  return { from: startOfDayIso(firstDay), to: startOfDayIso(addDays(lastDay, 1)), firstDay, lastDay };
}
export function weekWindow(weekStart: string): { from: string; to: string; days: string[] } {
  const days = Array.from({ length: 7 }, (_, i) => addDays(weekStart, i));
  return { from: startOfDayIso(weekStart), to: startOfDayIso(addDays(weekStart, 7)), days };
}

// ── العناصر وألوانُها ──────────────────────────────────────────────────────

export type CalendarKind = 'SESSION' | 'COUNSELING' | 'CLASS_PERIOD' | 'CONSULTATION';

export interface CalendarItem {
  id: string;
  kind: CalendarKind;
  titleAr: string;
  subtitleAr: string | null;
  startsAt: string;
  endsAt: string;
  status: string | null;
  href: string | null;
  refs: { studentId?: string; staffId?: string; classroomId?: string };
}
export interface CalendarResponse {
  from: string; to: string; kinds: CalendarKind[]; staff: { id: string; fullName: string } | null; items: CalendarItem[];
  /** العطلُ الرسمية وعطلُ المؤسسة في النافذة (0084) — يومٌ كامل لا موعد */
  holidays?: Array<{ id: string; day: string; nameAr: string; approximate: boolean; own: boolean }>;
}

/**
 * لكل نوعٍ لونُه — طبقةُ توكنزٍ دلالية (`--cal-session` …) معرَّفةٌ في `tokens.css` من ألوان الهوية نفسِها،
 * فلا قيمةَ جديدة، والليلُ يعيد تعريفَها وحده. الاسمُ هنا للمفتاح والشرح، والصنفُ يحمل اللون.
 */
export const CALENDAR_KINDS: Record<CalendarKind, { labelAr: string; className: string; order: number }> = {
  SESSION: { labelAr: 'جلسة تربية خاصة', className: 'kind-session', order: 0 },
  COUNSELING: { labelAr: 'جلسة إرشاد', className: 'kind-counseling', order: 1 },
  CLASS_PERIOD: { labelAr: 'حصة', className: 'kind-period', order: 2 },
  CONSULTATION: { labelAr: 'استشارة', className: 'kind-consultation', order: 3 },
};
export const KIND_ORDER: CalendarKind[] = ['SESSION', 'COUNSELING', 'CLASS_PERIOD', 'CONSULTATION'];

export function groupByDay(items: CalendarItem[]): Map<string, CalendarItem[]> {
  const m = new Map<string, CalendarItem[]>();
  for (const it of items) { const k = dayKeyOf(new Date(it.startsAt)); m.set(k, [...(m.get(k) ?? []), it]); }
  return m;
}

/** الأنواعُ الحاضرة في يومٍ بترتيبٍ ثابت — للنقاط تحت رقم اليوم */
export function kindsOfDay(items: CalendarItem[] | undefined): CalendarKind[] {
  const set = new Set<CalendarKind>((items ?? []).map((i) => i.kind));
  return KIND_ORDER.filter((k) => set.has(k));
}

/** مدى الساعات المعروض في الأسبوع: 07–17 على الأقل، ويتّسع لما يخرج عنه */
export function hourRange(items: CalendarItem[]): { start: number; end: number } {
  let start = 7 * 60; let end = 17 * 60;
  for (const it of items) {
    const s = minutesOf(new Date(it.startsAt));
    const e = s + Math.max(15, Math.round((new Date(it.endsAt).getTime() - new Date(it.startsAt).getTime()) / 60_000));
    if (s < start) start = Math.floor(s / 60) * 60;
    if (e > end) end = Math.min(24 * 60, Math.ceil(e / 60) * 60);
  }
  return { start, end };
}

/**
 * توزيعُ عناصر يومٍ على أعمدةٍ متجاورة حين تتداخل أوقاتُها — كل عنصرٍ يأخذ عمودَه وعددَ الأعمدة في مجموعته،
 * فالمتداخلان يتقاسمان العرضَ ولا يغطّي أحدُهما الآخر.
 */
export function layoutDay(items: CalendarItem[]): Array<{ item: CalendarItem; top: number; height: number; col: number; cols: number }> {
  const rows = items
    .map((item) => {
      const top = minutesOf(new Date(item.startsAt));
      const height = Math.max(15, Math.round((new Date(item.endsAt).getTime() - new Date(item.startsAt).getTime()) / 60_000));
      return { item, top, height, col: 0, cols: 1 };
    })
    .sort((a, b) => a.top - b.top || b.height - a.height);
  // مجموعاتُ التداخل: سلسلةٌ من العناصر كلُّ واحدٍ يتداخل مع ما قبله في المجموعة
  let group: typeof rows = []; let groupEnd = -1;
  const flush = () => {
    const colEnds: number[] = [];
    for (const r of group) {
      let c = colEnds.findIndex((end) => end <= r.top);
      if (c === -1) { c = colEnds.length; colEnds.push(0); }
      colEnds[c] = r.top + r.height; r.col = c;
    }
    for (const r of group) r.cols = colEnds.length;
    group = []; groupEnd = -1;
  };
  for (const r of rows) {
    if (group.length && r.top >= groupEnd) flush();
    group.push(r); groupEnd = Math.max(groupEnd, r.top + r.height);
  }
  if (group.length) flush();
  return rows;
}
